import React, { useEffect, useState } from 'react';
import { clients } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const clientOffsets = [
  { x: -8, y: -4, rotate: -3 },
  { x: 6, y: 4, rotate: 2 },
  { x: -5, y: 0, rotate: 2 },
  { x: 9, y: -3, rotate: -2 },
  { x: -10, y: 6, rotate: -2 },
  { x: 5, y: -2, rotate: 3 },
  { x: -6, y: 5, rotate: -3 },
  { x: 8, y: 1, rotate: 2 },
  { x: -7, y: -1, rotate: 2 },
  { x: 7, y: 7, rotate: -2 },
  { x: -4, y: 2, rotate: 1 },
  { x: 8, y: -1, rotate: -3 }
];

const logoScaleOverrides = {
  Alejandroperez: 1.6,
  BeeaniEnglish: 1.72,
  'Colegio Más Luz': 1.6,
  EDT: 1.72,
  EDTPLUS: 1.45,
  'Estudio Suken': 1.9,
  Indawork: 1.5,
  'Joaquin Marker': 1.95,
  'Luma profesional': 1.5,
  QueMate: 2.2,
  Rolitornillos: 1.95,
  kukyMotos: 1.5
};

const ClientsGrid = () => {
  const { t } = useLanguage();
  const [headerRef, headerVisible] = useScrollAnimation();
  const [gridRef, gridVisible] = useScrollAnimation();
  const [isSmall, setIsSmall] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsSmall(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p
            className="inline-block rounded-full border border-black/15 bg-black/5 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-black/60 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('clients.badge')}
          </p>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">{t('clients.title')}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('clients.description')}
          </p>
        </div>

        <div
          ref={gridRef}
          className="mx-auto mb-14 grid max-w-[980px] grid-cols-2 gap-x-5 gap-y-10 sm:grid-cols-3 sm:gap-x-4 sm:gap-y-10 md:mb-20 md:grid-cols-4 md:gap-x-5 md:gap-y-12 lg:grid-cols-6 lg:gap-x-6"
        >
          {clients.map((client, index) => {
            const offset = clientOffsets[index % clientOffsets.length];
            const metaLabel = client.handle || client.subscribers;
            const logoScale = logoScaleOverrides[client.name] ?? 1.6;
            const offsetScale = isSmall ? 0.3 : 1;
            const rotateScale = isSmall ? 0.5 : 1;
            const logoScaleMobile = isSmall ? 0.72 : 1;
            const basePath =
              typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL
                ? process.env.PUBLIC_URL.replace(/\/$/, '')
                : '';

            let rawLogoSrc = null;
            if (client.logoSrc) {
              // Absolute URLs or data URIs: use as-is
              if (/^https?:\/\//.test(client.logoSrc) || client.logoSrc.startsWith('data:')) {
                rawLogoSrc = client.logoSrc;
              } else if (client.logoSrc.startsWith('/')) {
                // Rooted paths: respect PUBLIC_URL (basePath)
                rawLogoSrc = `${basePath}${client.logoSrc}`;
              } else {
                // Relative paths like './logos/...' or 'logos/...'
                rawLogoSrc = `${basePath}/${client.logoSrc.replace(/^(\.\/)+/, '')}`;
              }
            }

            const logoSrcResolved = rawLogoSrc ? encodeURI(rawLogoSrc) : null;

            return (
              <div
                key={client.name}
                className="group relative mx-auto flex h-[176px] w-full max-w-[150px] items-start justify-center pt-3 transition-[transform,opacity] duration-700 ease-out sm:h-[202px] sm:max-w-[176px] md:h-[210px]"
                style={{
                  opacity: gridVisible ? 1 : 0,
                  transform: gridVisible ? 'translateY(0)' : 'translateY(24px)',
                  transitionDelay: gridVisible ? `${index * 60}ms` : '0ms'
                }}
              >
                <div
                  className="relative"
                  style={{
                    transform: `translate3d(${offset.x * offsetScale}px, ${offset.y * offsetScale}px, 0) rotate(${offset.rotate * rotateScale}deg)`
                  }}
                >
                  <div
                    aria-label={`${client.name} ${metaLabel}`}
                    className="relative flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-[1.03]"
                  >
                    <div className="pointer-events-none absolute -inset-3 rounded-[1.35rem] bg-white/92 opacity-0 shadow-[0_18px_32px_rgba(15,23,42,0.14)] transition-all duration-300 group-hover:opacity-100" />
                    {logoSrcResolved ? (
                      <img
                        src={logoSrcResolved}
                        alt={client.name}
                        loading="lazy"
                        decoding="async"
                        className="relative z-10 h-[104px] w-[104px] object-contain drop-shadow-[0_10px_20px_rgba(15,23,42,0.18)] md:h-[140px] md:w-[140px] animate-soft-float motion-reduce:animate-none"
                        style={{
                          background: 'transparent',
                          transform: `scale(${logoScale * logoScaleMobile})`,
                          transformOrigin: 'center center',
                          animationDelay: `${(index % 6) * 160}ms`,
                          animationDuration: '4200ms'
                        }}
                      />
                    ) : (
                      <span>{client.logo}</span>
                    )}
                  </div>
                </div>

                <div className="pointer-events-none absolute bottom-0 left-1/2 z-20 w-[170px] -translate-x-1/2 rounded-[1rem] border border-zinc-200 bg-white/95 px-2.5 py-2 text-center opacity-0 shadow-[0_16px_34px_rgba(15,23,42,0.12)] backdrop-blur-sm translate-y-2 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 sm:w-[176px]">
                  <div className="text-[12px] font-semibold leading-tight text-zinc-900">{client.name}</div>
                  <div className="mt-1 text-[8px] uppercase tracking-[0.14em] text-zinc-500">{metaLabel}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Stats */}
        <div className="grid md:grid-cols-3 gap-8 mt-20 pt-20 border-t border-gray-200">
          <div className="text-center">
            <div className="text-5xl font-bold mb-2">+75M</div>
            <div className="text-gray-600">{t('clients.statVisits')}</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold mb-2">45+</div>
            <div className="text-gray-600">{t('clients.statClients')}</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold mb-2">5.0★</div>
            <div className="text-gray-600">{t('clients.statRating')}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientsGrid;